import React, { useEffect, useMemo, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { enGB } from "date-fns/locale/en-GB";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store/index";
import { openModal } from "store/modals/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalTypes } from "configs/types";
import {
  getPaymentInfo,
  getPlayerItem,
  handleDepositsClear,
  handleInfoClear,
} from "store/players/list/slice";
import { getCurrencyList, getPayoutCurrencyList } from "store/settings/slice";
import { unionPaymentConfigurationCurrencies } from "helpers/paymentConfigurationCurrencies";

import Button from "components/buttons/Button";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import { getDatePickerFormats, useTimeFormat } from "helpers/timeFormat";
import { handleApiError } from "helpers/handleApiError";

import infoIcon from "assets/icons/info.svg";
import bonusImageFallback from "assets/images/image_not_found.webp";
import { Modal } from "components/Modal/Modal";
import styles from "./FinanceActionModal.module.scss";

registerLocale("en-GB", enGB);

type FormErrors = Partial<
  Record<
    | "amount"
    | "currency"
    | "paymentMethodId"
    | "wagerMultiplier"
    | "depositId"
    | "amountPaid"
    | "reason",
    string
  >
>;

const DECIMAL_PATTERN = /^\d+(?:\.\d+)?$/;
type DepositType = "regular" | "reconciliation";

interface CurrencyRate {
  currencyFrom: string;
  rate: number | string | null;
  status?: string;
}

interface ReconciliationDeposit {
  id: number;
  amountAttempt: number | string;
  currency: string;
  paymentMethod?: string;
  paymentMethodTitle?: string;
  paymentProviderTitle?: string;
  providerTitle?: string;
  status?: string;
}

interface DepositEligibleBonus {
  bonusId: number;
  userBonusId?: number | null;
  title: string;
  image?: string | null;
  images?: {
    desktop?: string | null;
    mobile?: string | null;
  };
}

type SelectedBonusReference =
  | { bonusId: number; userBonusId?: never }
  | { bonusId?: never; userBonusId: number };

const sanitizeDecimal = (value: string, decimalPlaces?: number) => {
  const sanitized = value.replace(/[^\d.]/g, "");
  const [integerPart = "", ...decimalParts] = sanitized.split(".");
  const hasDecimalPoint = sanitized.includes(".");
  const decimalPart = decimalParts
    .join("")
    .slice(0, decimalPlaces ?? Number.POSITIVE_INFINITY);
  const normalizedIntegerPart =
    hasDecimalPoint && integerPart === "" ? "0" : integerPart;

  return hasDecimalPoint
    ? `${normalizedIntegerPart}.${decimalPart}`
    : normalizedIntegerPart;
};

const CreateDeposit: React.FC = () => {
  const { t } = useTranslation();
  const datePickerFormats = getDatePickerFormats(useTimeFormat());
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const player = useAppSelector((store) => store.players.payload);
  const currenciesList = useAppSelector((store) => store.settings.currencies);
  const payoutCurrencies = useAppSelector(
    (store) => store.settings.payoutCurrencies,
  );
  const paymentCurrencies = useMemo(
    () => unionPaymentConfigurationCurrencies(currenciesList, payoutCurrencies),
    [currenciesList, payoutCurrencies],
  );
  const [amount, setAmount] = useState("");
  const [depositType, setDepositType] = useState<DepositType>("regular");
  const [currency, setCurrency] = useState("");
  const [currencyRates, setCurrencyRates] = useState<CurrencyRate[]>([]);
  const [paymentMethodId, setPaymentMethodId] = useState<string | number>("");
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [eligibleBonuses, setEligibleBonuses] = useState<
    DepositEligibleBonus[]
  >([]);
  const [selectedBonusKey, setSelectedBonusKey] = useState("");
  const [isLoadingBonuses, setIsLoadingBonuses] = useState(false);
  const [wagerMultiplier, setWagerMultiplier] = useState("");
  const [reason, setReason] = useState("");
  const [externalPaymentId, setExternalPaymentId] = useState("");
  const [externalTransactionId, setExternalTransactionId] = useState("");
  const [deposits, setDeposits] = useState<ReconciliationDeposit[]>([]);
  const [depositId, setDepositId] = useState<string | number>("");
  const [isLoadingDeposits, setIsLoadingDeposits] = useState(false);
  const [amountPaid, setAmountPaid] = useState("");
  const [providerCompletedAt, setProviderCompletedAt] = useState<Date | null>(
    null,
  );
  const [internalComment, setInternalComment] = useState("");
  const [transactionCode, setTransactionCode] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isConfirming, setConfirming] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  const userId = Number(searchParams.get("entityId"));
  const isPoker = [
    "bo.planets.casino",
    "backoffice-staging.planets.casino",
  ].includes(window.location.hostname);
  const platformCurrencies = useMemo(
    () =>
      paymentCurrencies
        .filter((code) => !isPoker || code === "USD")
        .map((code) => ({ code })),
    [paymentCurrencies, isPoker],
  );
  const currencyOptions = platformCurrencies.map(({ code }) => ({
    value: code,
    label: code,
  }));
  const conversionCurrencies = useMemo(
    () =>
      currenciesList
        .filter((code) => code !== currency)
        .map((code) => ({ code })),
    [currenciesList, currency],
  );
  const convertedAmounts = useMemo(() => {
    const enteredAmount = Number(amount);
    if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) return [];

    return conversionCurrencies.flatMap((conversionCurrency) => {
      const rate = currencyRates.find(
        (item) => item.currencyFrom === conversionCurrency.code,
      );
      const numericRate = Number(rate?.rate);

      if (
        rate?.status !== "available" ||
        !Number.isFinite(numericRate) ||
        numericRate <= 0
      ) {
        return [];
      }

      return [
        {
          currency: conversionCurrency.code,
          amount: (enteredAmount / numericRate).toLocaleString(undefined, {
            maximumFractionDigits: 2,
          }),
        },
      ];
    });
  }, [amount, conversionCurrencies, currencyRates]);
  const selectedPaymentMethodLabel =
    paymentMethodOptions.find(
      (option) => String(option.value) === String(paymentMethodId),
    )?.label ?? "";
  const selectedDeposit = deposits.find(
    (deposit) => String(deposit.id) === String(depositId),
  );
  const isReconciliation = depositType === "reconciliation";
  const depositOptions = deposits.map((deposit) => ({
    value: deposit.id,
    label: `#${deposit.id} · ${deposit.amountAttempt} ${deposit.currency} · ${deposit.status ?? ""}`,
  }));
  const bonusOptions = eligibleBonuses.map((bonus) => ({
    value:
      bonus.userBonusId !== undefined && bonus.userBonusId !== null
        ? `userBonus:${bonus.userBonusId}`
        : `bonus:${bonus.bonusId}`,
    label: bonus.title,
    img: bonus.images?.desktop || bonus.image || bonusImageFallback,
  }));
  const selectedBonusLabel =
    bonusOptions.find(
      (option) => String(option.value) === String(selectedBonusKey),
    )?.label ?? t("noBonus");

  useEffect(() => {
    if (userId && player?.id !== userId) {
      dispatch(getPlayerItem(userId));
    }
  }, [dispatch, player?.id, userId]);

  useEffect(() => {
    dispatch(getCurrencyList());
    dispatch(getPayoutCurrencyList());
  }, [dispatch]);

  useEffect(() => {
    if (!currency || !conversionCurrencies.length) {
      setCurrencyRates([]);
      return;
    }

    const abortController = new AbortController();
    setCurrencyRates([]);

    instance
      .post(
        apiRoutes.paymentCurrenciesRatesMap,
        {
          currencyFrom: conversionCurrencies.map(({ code }) => code),
          baseCurrency: currency,
          includeMissing: 1,
        },
        { signal: abortController.signal },
      )
      .then((result) => {
        if (abortController.signal.aborted) return;
        const data = result?.data;
        const items = Array.isArray(data)
          ? data
          : (data?.items ?? data?.rates ?? []);
        setCurrencyRates(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!abortController.signal.aborted) setCurrencyRates([]);
      });

    return () => abortController.abort();
  }, [currency, conversionCurrencies]);

  useEffect(() => {
    if (!currency) {
      setPaymentMethodOptions([]);
      setPaymentMethodId("");
      return;
    }

    setIsLoadingPaymentMethods(true);
    setPaymentMethodId("");
    setPaymentMethodOptions([]);

    instance
      .post(apiRoutes.paymentMethodList, {
        limit: 20,
        offset: 0,
        transactionType: "deposit",
        currency: [currency],
        configIsActive: 1,
        order: {
          isActive: "asc",
          id: "asc",
        },
      })
      .then((result) => {
        const items = result?.data?.items ?? [];
        setPaymentMethodOptions(
          items.map((method: { id: number; title: string }) => ({
            value: method.id,
            label: method.title,
          })),
        );
      })
      .catch(() => {
        setPaymentMethodOptions([]);
      })
      .finally(() => {
        setIsLoadingPaymentMethods(false);
      });
  }, [currency]);

  useEffect(() => {
    setSelectedBonusKey("");
    setEligibleBonuses([]);

    if (!userId || !currency || isReconciliation) {
      setIsLoadingBonuses(false);
      return;
    }

    const abortController = new AbortController();
    setIsLoadingBonuses(true);

    instance
      .get(
        `${apiRoutes.playerBonuses}/${userId}/deposit-eligible?currency=${encodeURIComponent(currency)}&locale=en-US`,
        { signal: abortController.signal },
      )
      .then((result) => {
        if (abortController.signal.aborted) return;

        const data = result?.data;
        setEligibleBonuses(Array.isArray(data) ? data : (data?.items ?? []));
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setEligibleBonuses([]);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoadingBonuses(false);
        }
      });

    return () => abortController.abort();
  }, [currency, isReconciliation, userId]);

  useEffect(() => {
    if (!isReconciliation || !userId) return;

    setIsLoadingDeposits(true);
    instance
      .post(apiRoutes.paymenDepositsList, {
        limit: 100,
        offset: 0,
        userIds: [userId],
        order: { id: "desc" },
      })
      .then((result) => setDeposits(result?.data?.items ?? []))
      .catch(() => setDeposits([]))
      .finally(() => setIsLoadingDeposits(false));
  }, [isReconciliation, userId]);

  const handleDepositTypeChange = (value: string | number) => {
    setDepositType(value as DepositType);
    setErrors({});
    setConfirming(false);
  };

  const getSelectedBonusReference = (): SelectedBonusReference | undefined => {
    if (!selectedBonusKey) return undefined;

    const [referenceType, id] = selectedBonusKey.split(":");
    const numericId = Number(id);

    return referenceType === "userBonus"
      ? { userBonusId: numericId }
      : { bonusId: numericId };
  };

  const handleCloseClick = () => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.player}&entityId=${searchParams.get("entityId")}&tab=finance`,
    );
    dispatch(openModal({ activeModal: ModalTypes.player }));
  };

  const clearError = (field: keyof FormErrors) => {
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = () => {
    const nextErrors: FormErrors = {};
    const normalizedReason = reason.trim();

    if (isReconciliation) {
      if (!depositId) nextErrors.depositId = t("requiredField");
      if (!amountPaid) {
        nextErrors.amountPaid = t("requiredField");
      } else if (!DECIMAL_PATTERN.test(amountPaid) || Number(amountPaid) <= 0) {
        nextErrors.amountPaid = t("invalidAmount");
      }
    } else if (!amount) {
      nextErrors.amount = t("requiredField");
    } else if (!DECIMAL_PATTERN.test(amount) || Number(amount) <= 0) {
      nextErrors.amount = t("invalidAmount");
    }

    if (!isReconciliation && !currency) {
      nextErrors.currency = t("requiredField");
    }

    if (!isReconciliation && !paymentMethodId) {
      nextErrors.paymentMethodId = t("requiredField");
    }

    if (!isReconciliation) {
      if (!wagerMultiplier) {
        nextErrors.wagerMultiplier = t("requiredField");
      } else if (!DECIMAL_PATTERN.test(wagerMultiplier)) {
        nextErrors.wagerMultiplier = t(
          isPoker ? "invalidPokerWagerMultiplier" : "invalidWagerMultiplier",
        );
      } else if (!isPoker && Number(wagerMultiplier) <= 0) {
        nextErrors.wagerMultiplier = t("invalidWagerMultiplier");
      }
    }

    if (!normalizedReason) {
      nextErrors.reason = t("requiredField");
    } else if (normalizedReason.length > 255) {
      nextErrors.reason = t("reasonMaxLength");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleRequestConfirmation = () => {
    if (validate()) {
      setConfirming(true);
    }
  };

  const handleApply = async () => {
    if (!userId || isSubmitting) return;

    setSubmitting(true);
    try {
      if (isReconciliation) {
        await instance.post(apiRoutes.paymentDepositReconciliation, {
          depositId: Number(depositId),
          amountPaid,
          reason: reason.trim(),
          ...(providerCompletedAt && {
            providerCompletedAt: providerCompletedAt.toISOString(),
          }),
          ...(internalComment.trim() && {
            internalComment: internalComment.trim(),
          }),
          ...(transactionCode.trim() && {
            transactionCode: transactionCode.trim(),
          }),
          ...(externalPaymentId.trim() && { externalPaymentId }),
          ...(externalTransactionId.trim() && {
            externalTransactionId,
          }),
        });
      } else {
        const selectedBonusReference = getSelectedBonusReference();

        await instance.post(apiRoutes.paymentManualDeposit, {
          userId,
          amount,
          currency,
          wagerMultiplier,
          reason: reason.trim(),
          paymentMethodId: Number(paymentMethodId),
          ...(selectedBonusReference ?? {}),
          ...(externalPaymentId.trim() && {
            externalPaymentId: externalPaymentId.trim(),
          }),
          ...(externalTransactionId.trim() && {
            externalTransactionId: externalTransactionId.trim(),
          }),
        });
      }
      dispatch(
        showNotification({
          title: t("success"),
          message: t(isReconciliation ? "depositReconciled" : "depositCreated"),
          type: NotificationTypes.success,
        }),
      );
      dispatch(handleDepositsClear());
      dispatch(handleInfoClear());
      await Promise.all([
        dispatch(getPlayerItem(userId)),
        dispatch(getPaymentInfo(String(userId))),
      ]);
      navigate(
        `${location.pathname}?activeModal=${ModalTypes.player}&entityId=${searchParams.get("entityId")}&tab=finance`,
      );
      dispatch(openModal({ activeModal: ModalTypes.player }));
    } catch (error) {
      setConfirming(false);
      handleApiError({ error, t, dispatch });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header className={styles.modalHeader}>
        <p>{t("createManualDeposit")}</p>
      </Modal.Header>
      <Modal.Content className={styles.modalContent}>
        {isConfirming ? (
          <div className={styles.confirmation}>
            <h3>{t("confirmManualDeposit")}</h3>
            <p>
              {t(
                isReconciliation
                  ? "reconciliationConfirmation"
                  : "manualDepositConfirmation",
              )}
            </p>
            <dl>
              <div>
                <dt>{t(isReconciliation ? "confirmedAmount" : "amount")}</dt>
                <dd>
                  {isReconciliation ? amountPaid : amount}{" "}
                  {isReconciliation ? selectedDeposit?.currency : currency}
                </dd>
              </div>
              {isReconciliation ? (
                <>
                  <div>
                    <dt>{t("originalDepositId")}</dt>
                    <dd>{depositId}</dd>
                  </div>
                  <div>
                    <dt>{t("currency")}</dt>
                    <dd>{selectedDeposit?.currency}</dd>
                  </div>
                  <div>
                    <dt>{t("paymentMethod")}</dt>
                    <dd>
                      {selectedDeposit?.paymentMethodTitle ??
                        selectedDeposit?.paymentMethod ??
                        "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>{t("provider")}</dt>
                    <dd>
                      {selectedDeposit?.paymentProviderTitle ??
                        selectedDeposit?.providerTitle ??
                        "—"}
                    </dd>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <dt>{t("currency")}</dt>
                    <dd>{currency}</dd>
                  </div>
                  <div>
                    <dt>{t("paymentMethod")}</dt>
                    <dd>{selectedPaymentMethodLabel || paymentMethodId}</dd>
                  </div>
                  <div>
                    <dt>{t("bonus")}</dt>
                    <dd>{selectedBonusLabel}</dd>
                  </div>
                  <div>
                    <dt>{t("wagerMultiplier")}</dt>
                    <dd>{wagerMultiplier}</dd>
                  </div>
                </>
              )}
              <div>
                <dt>{t("reason")}</dt>
                <dd>{reason.trim()}</dd>
              </div>
              {externalPaymentId.trim() && (
                <div>
                  <dt>{t("externalPaymentId")}</dt>
                  <dd>{externalPaymentId.trim()}</dd>
                </div>
              )}
              {externalTransactionId.trim() && (
                <div>
                  <dt>{t("externalTransactionId")}</dt>
                  <dd>{externalTransactionId.trim()}</dd>
                </div>
              )}
              {isReconciliation && providerCompletedAt && (
                <div>
                  <dt>{t("providerCompletedAt")}</dt>
                  <dd>{providerCompletedAt.toISOString()}</dd>
                </div>
              )}
              {isReconciliation && internalComment.trim() && (
                <div>
                  <dt>{t("internalComment")}</dt>
                  <dd>{internalComment.trim()}</dd>
                </div>
              )}
              {isReconciliation && transactionCode.trim() && (
                <div>
                  <dt>{t("transactionCode")}</dt>
                  <dd>{transactionCode.trim()}</dd>
                </div>
              )}
            </dl>
          </div>
        ) : (
          <div className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.amountField}>
                <InnerInput
                  type="text"
                  label={t("amount")}
                  placeholder={t("enterAmount")}
                  value={
                    isReconciliation
                      ? selectedDeposit
                        ? String(selectedDeposit.amountAttempt)
                        : ""
                      : amount
                  }
                  onChange={(value) => {
                    setAmount(sanitizeDecimal(value));
                    clearError("amount");
                  }}
                  id="manualDepositAmount"
                  inputMode="decimal"
                  isRequired
                  disabled={isReconciliation}
                  tooltipText={
                    isReconciliation
                      ? undefined
                      : t("manualDepositAmountTooltip")
                  }
                  tooltipPosition="right"
                  hasError={Boolean(errors.amount)}
                  title={errors.amount}
                />
              </div>
              {isReconciliation ? (
                <>
                  <InnerInput
                    type="text"
                    label={t("currency")}
                    value={selectedDeposit?.currency ?? ""}
                    placeholder={t("chooseCurrency")}
                    onChange={() => undefined}
                    id="reconciliationCurrency"
                    isRequired
                    disabled
                  />
                  <InnerInput
                    type="text"
                    label={t("paymentMethod")}
                    value={
                      selectedDeposit?.paymentMethodTitle ??
                      selectedDeposit?.paymentMethod ??
                      ""
                    }
                    placeholder={t("selectPaymentMethod")}
                    onChange={() => undefined}
                    id="reconciliationPaymentMethod"
                    isRequired
                    disabled
                  />
                </>
              ) : (
                <>
                  <DefaultDropdown
                    label={t("currency")}
                    placeholder={t("chooseCurrency")}
                    value={currency}
                    options={currencyOptions}
                    onSelect={(value) => {
                      setCurrency(String(value));
                      clearError("currency");
                      clearError("paymentMethodId");
                    }}
                    isRequired
                    hasError={Boolean(errors.currency)}
                    tooltipText={t("manualDepositCurrencyTooltip")}
                    tooltipPosition="right"
                  />
                  <DefaultDropdown
                    label={t("paymentMethod")}
                    placeholder={t("selectPaymentMethod")}
                    value={paymentMethodId}
                    options={paymentMethodOptions}
                    onSelect={(value) => {
                      setPaymentMethodId(value);
                      clearError("paymentMethodId");
                    }}
                    disabled={!currency}
                    loading={isLoadingPaymentMethods}
                    isRequired
                    hasError={Boolean(errors.paymentMethodId)}
                    tooltipText={t("manualDepositPaymentMethodTooltip")}
                    tooltipPosition="right"
                  />
                </>
              )}
            </div>

            {!isReconciliation && convertedAmounts.length > 0 && (
              <div className={styles.currencyEquivalents} aria-live="polite">
                {convertedAmounts.map((item) => (
                  <span key={item.currency}>
                    ≈ {item.amount} {item.currency}
                  </span>
                ))}
              </div>
            )}

            <div
              className={`${styles.depositTypeRow} ${
                isReconciliation ? styles.depositTypeRowSingle : ""
              }`}
            >
              <DefaultDropdown
                label={t("depositType")}
                value={depositType}
                options={[
                  { value: "regular", label: t("regular") },
                  { value: "reconciliation", label: t("reconciliation") },
                ]}
                onSelect={handleDepositTypeChange}
                isRequired
                tooltipText={t("depositTypeTooltip")}
                tooltipPosition="right"
              />
              {!isReconciliation && (
                <DefaultDropdown
                  label={t("bonusOptional")}
                  placeholder={t("selectAvailableBonus")}
                  value={selectedBonusKey}
                  options={bonusOptions}
                  onSelect={(value) => setSelectedBonusKey(String(value))}
                  disabled={!currency}
                  loading={isLoadingBonuses}
                  emptyText={t("noBonusesAvailable")}
                  useFixedMenu
                  menuMaxHeight={230}
                  isEllipsis
                />
              )}
            </div>

            {!isReconciliation ? (
              <InnerInput
                type="text"
                label={t("wagerMultiplier")}
                placeholder={t("enterWagerMultiplier")}
                value={wagerMultiplier}
                onChange={(value) => {
                  setWagerMultiplier(sanitizeDecimal(value, 2));
                  clearError("wagerMultiplier");
                }}
                id="manualDepositWagerMultiplier"
                inputMode="decimal"
                isRequired
                tooltipText={t(
                  isPoker
                    ? "manualDepositPokerWagerMultiplierTooltip"
                    : "manualDepositWagerMultiplierTooltip",
                )}
                tooltipPosition="right"
                hasError={Boolean(errors.wagerMultiplier)}
                title={errors.wagerMultiplier}
              />
            ) : (
              <>
                <DefaultDropdown
                  label={t("originalDepositId")}
                  placeholder={t("selectOriginalDeposit")}
                  value={depositId}
                  options={depositOptions}
                  onSelect={(value) => {
                    setDepositId(value);
                    clearError("depositId");
                  }}
                  loading={isLoadingDeposits}
                  isRequired
                  hasError={Boolean(errors.depositId)}
                  useFixedMenu
                  tooltipText={t("originalDepositTooltip")}
                  tooltipPosition="right"
                />
                {selectedDeposit && (
                  <InnerInput
                    type="text"
                    label={t("provider")}
                    value={
                      selectedDeposit.paymentProviderTitle ??
                      selectedDeposit.providerTitle ??
                      "—"
                    }
                    onChange={() => undefined}
                    id="reconciliationProvider"
                    disabled
                  />
                )}
                <div className={styles.reconciliationRow}>
                  <InnerInput
                    type="text"
                    label={t("confirmedAmount")}
                    placeholder={t("enterConfirmedAmount")}
                    value={amountPaid}
                    onChange={(value) => {
                      setAmountPaid(sanitizeDecimal(value));
                      clearError("amountPaid");
                    }}
                    id="reconciliationAmountPaid"
                    inputMode="decimal"
                    isRequired
                    hasError={Boolean(errors.amountPaid)}
                    title={errors.amountPaid}
                    tooltipText={t("confirmedAmountTooltip")}
                    tooltipPosition="right"
                  />
                  <div
                    className={`date-picker-wrapper ${styles.datePickerField}`}
                  >
                    <div className="title">
                      {t("providerCompletedAtOptional")}
                    </div>
                    <DatePicker
                      customInput={<ReadOnlyInput />}
                      locale="en-GB"
                      selected={providerCompletedAt}
                      onChange={(date) => setProviderCompletedAt(date)}
                      showTimeSelect
                      timeFormat={datePickerFormats.timeFormat}
                      dateFormat={datePickerFormats.dateFormat}
                      placeholderText={datePickerFormats.placeholder}
                      isClearable
                    />
                  </div>
                </div>
              </>
            )}
            <InnerInput
              type="textarea"
              label={t("reason")}
              placeholder={t("enterManualDepositReason")}
              value={reason}
              onChange={(value) => {
                setReason(value);
                clearError("reason");
              }}
              id="manualDepositReason"
              isRequired
              rows={7}
              tooltipText={t("manualDepositReasonTooltip")}
              tooltipPosition="right"
              hasError={Boolean(errors.reason)}
              title={errors.reason}
            />
            {isReconciliation && (
              <>
                <InnerInput
                  type="textarea"
                  label={t("internalCommentOptional")}
                  placeholder={t("enterInternalComment")}
                  value={internalComment}
                  onChange={setInternalComment}
                  id="reconciliationInternalComment"
                  rows={4}
                  tooltipText={t("internalCommentTooltip")}
                  tooltipPosition="right"
                />
                <InnerInput
                  type="text"
                  label={t("transactionCodeOptional")}
                  placeholder={t("enterTransactionCode")}
                  value={transactionCode}
                  onChange={setTransactionCode}
                  id="reconciliationTransactionCode"
                  tooltipText={t("transactionCodeTooltip")}
                  tooltipPosition="right"
                />
              </>
            )}
            <InnerInput
              type="text"
              label={t("externalPaymentIdOptional")}
              placeholder={t("enterExternalPaymentId")}
              value={externalPaymentId}
              onChange={setExternalPaymentId}
              id="manualDepositExternalPaymentId"
              tooltipText={t("manualDepositExternalPaymentIdTooltip")}
              tooltipPosition="right"
            />
            <InnerInput
              type="text"
              label={t("externalTransactionIdOptional")}
              placeholder={t("enterExternalTransactionId")}
              value={externalTransactionId}
              onChange={setExternalTransactionId}
              id="manualDepositExternalTransactionId"
              tooltipText={t("manualDepositExternalTransactionIdTooltip")}
              tooltipPosition="right"
            />

            <div className={styles.notice}>
              <img src={infoIcon} alt="" aria-hidden="true" />
              <span>
                {t(
                  isReconciliation
                    ? "reconciliationNotice"
                    : "manualDepositNotice",
                )}
              </span>
            </div>
          </div>
        )}
      </Modal.Content>
      <Modal.Footer>
        {isConfirming ? (
          <>
            <Button
              isSecondary
              txt={t("back")}
              handleClick={() => setConfirming(false)}
              disabled={isSubmitting}
            />
            <Button
              isGreen
              handleClick={handleApply}
              txt={t("confirm")}
              isLoad={isSubmitting}
            />
          </>
        ) : (
          <>
            <Button
              isSecondary
              txt={t("cancel")}
              handleClick={handleCloseClick}
            />
            <Button
              isGreen
              handleClick={handleRequestConfirmation}
              txt={t("create")}
              disabled={!userId}
            />
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateDeposit;
